import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, MenuItem, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { localGet } from "../../utils/localStorageUtils";
import { downloadText, getAccessLog, listDistricts } from "../../utils/accessLogService";

/**
 * District access log (Nov 2026, multi-tenant security). Who viewed student identities, handled
 * report-history access, changed staff/students/schools/rotations/roles, or downloaded data.
 * District admins: their own district. DiscloSure admins: choose a district.
 */
const CATEGORIES = [
  { value: "", label: "Everything" },
  { value: "identity", label: "Student identity viewed" },
  { value: "access", label: "Report-history access" },
  { value: "change", label: "Changes to people, schools and settings" },
  { value: "export", label: "Downloads" },
];

const isoDay = (d) => d.toISOString().slice(0, 10);

const AccessLog = () => {
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [district, setDistrict] = useState("");
  const [from, setFrom] = useState(isoDay(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(isoDay(new Date()));
  const [category, setCategory] = useState("");
  const [entries, setEntries] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => setUser(await localGet("user", true)))(); }, []);
  const isSuper = user?.permission === "disclosureAdmin";
  const allowed = isSuper || user?.permission === "districtAdmin";

  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try { setDistricts(await listDistricts()); } catch (e) { /* the box still accepts an id */ }
    })();
  }, [isSuper]);

  const params = useCallback(() => {
    const p = { from, to };
    if (category) p.category = category;
    if (isSuper && district) p.district = district;
    return p;
  }, [from, to, category, isSuper, district]);

  const load = useCallback(async () => {
    if (!allowed || (isSuper && !district)) return;
    setLoading(true); setError("");
    const res = await getAccessLog(params());
    if (res.ok) { setEntries(res.entries || []); setTruncated(!!res.truncated); }
    else { setEntries([]); setError(res.message); }
    setLoading(false);
  }, [allowed, isSuper, district, params]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    const res = await getAccessLog({ ...params(), format: "csv" });
    if (res.ok && res.csv) downloadText(res.csv, res.filename || "access-log.csv");
    else setError(res.message || "Couldn't download the access log.");
  };

  if (user && !allowed) {
    return <Box sx={{ p: '32px' }}><Typography>Only district administrators can view the access log.</Typography></Box>;
  }
  const head = { fontWeight: 700, color: theme.palette.custom?.black || "#000" };
  const fmt = (iso) => { if (!iso) return ""; const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`); return isNaN(d) ? iso : d.toLocaleString(); };

  return (
    // Box, not Container: the app layout removes all padding from Containers (page sat flush on the sidebar).
    <Box sx={{ p: '32px', pb: 6 }}>
      <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>Access log</Typography>
      <Typography sx={{ fontSize: "14px", color: theme.palette.text?.secondary, mt: 1, mb: 3, maxWidth: 900 }}>
        Who in your district viewed a student's identity, requested or approved access to a student's
        report history, added or changed staff, students, schools, on-call rotations or roles, and who
        downloaded data. Use it to answer questions like "who looked at my child's record?".
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        {isSuper && (
          districts.length ? (
            <TextField select size="small" label="District" value={district} onChange={(e) => setDistrict(e.target.value)} sx={{ minWidth: 260 }}>
              {districts.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          ) : (
            <TextField size="small" label="District ID" value={district} onChange={(e) => setDistrict(e.target.value.trim())} sx={{ minWidth: 260 }} />
          )
        )}
        <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField select size="small" label="Show" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 280 }}>
          {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={download} disabled={loading || !entries.length} sx={{ textTransform: "none" }}>
          Download (.csv)
        </Button>
      </Box>
      {isSuper && !district && <Typography sx={{ fontSize: "14px" }}>Choose a district to see its access log.</Typography>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {truncated && <Alert severity="info" sx={{ mb: 2 }}>Showing the most recent 5,000 entries. Narrow the dates to see older ones.</Alert>}
      {loading ? <CircularProgress /> : (
        (!isSuper || district) && (
          entries.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["When", "Who", "Role", "What", "Report #", "Student", "Details"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(e.when)}</TableCell>
                    <TableCell>{e.who}</TableCell>
                    <TableCell>{e.role}</TableCell>
                    <TableCell>{e.action}</TableCell>
                    <TableCell>{e.report}</TableCell>
                    <TableCell>{e.student}</TableCell>
                    <TableCell>{e.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : !error && <Typography sx={{ fontSize: "14px" }}>Nothing recorded for these dates.</Typography>
        )
      )}
    </Box>
  );
};

export default AccessLog;
